import { fail, pass, warn, type SeoRule } from '../rule';
import { T } from '../thresholds';
import { normalizeFr } from '../tokenize-fr';
import { SeoRuleKey, SeoScoreGroup } from '../types';

const G = SeoScoreGroup.IMAGES;

const GENERIC_ALT = new Set(['image', 'photo', 'picture', 'img', 'illustration', 'visuel', 'icone', 'icon', 'logo']);

export function isGenericAlt(alt: string): boolean {
  const norm = normalizeFr(alt)
    .replace(/[^a-z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (norm.length === 0) return true;
  if (GENERIC_ALT.has(norm)) return true;
  return /^(img|image|photo|dsc|screenshot|capture)[ _-]?\d+$/.test(norm);
}

function fileName(src: string): string {
  const path = src.split(/[?#]/)[0];
  const last = path.split('/').pop() ?? '';
  let decoded = last;
  try {
    decoded = decodeURIComponent(last);
  } catch {
    // Séquence « % » mal formée (ex. `promo-50%.jpg`) : on garde le nom brut plutôt que
    // de faire sauter tout le score sur une seule image.
  }
  return decoded.replace(/\.[a-z0-9]+$/i, '');
}

export function isBadFileName(src: string): boolean {
  const name = fileName(src);
  if (name.length === 0) return true;
  if (/^[a-f0-9]{16,}$/i.test(name)) return true;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(name)) return true;
  if (/^(img|image|dsc|photo|screenshot|capture|scan|pxl|dcim)?[ _-]?\d{3,}$/i.test(name)) return true;
  return !/[a-z]{3,}/i.test(name);
}

export const IMAGES_RULES: SeoRule[] = [
  {
    key: SeoRuleKey.IMAGE_PRESENT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      // Images choisies après la QA (moteur) : famille non évaluable plutôt que fausse pénalité.
      if (ctx.options.images_pending) return null;
      // Une seule image compte (2026.10.2) : l'article n'en affiche qu'une, le corps n'accepte pas `img`.
      const count = ctx.images.length;
      const threshold = '1 image';
      if (count === 0) return fail(0, threshold);
      return pass(count, threshold);
    },
  },
  {
    key: SeoRuleKey.IMAGE_ALT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.images.length === 0 || ctx.options.images_pending) return null;
      const bad = ctx.images.filter((i): boolean => {
        const alt = i.alt?.trim() ?? '';
        return alt.length < T.IMAGE_ALT_CHARS[0] || alt.length > T.IMAGE_ALT_CHARS[1] || isGenericAlt(alt);
      });
      const locations = bad.map((i): string => `${fileName(i.src) || i.src} : alt « ${i.alt ?? ''} »`);
      if (bad.length === 0) return pass('100 %');
      if (bad.length === 1) return warn(1, '0 alt manquant ou générique', locations);
      return fail(bad.length, '0 alt manquant ou générique', locations);
    },
  },
  {
    key: SeoRuleKey.IMAGE_FILENAME,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      if (ctx.images.length === 0 || ctx.options.images_pending) return null;
      const bad = ctx.images.filter((i): boolean => isBadFileName(i.src));
      if (bad.length === 0) return pass('100 % lisibles');
      return warn(
        `${bad.length}/${ctx.images.length} illisibles`,
        'noms de fichiers descriptifs',
        bad.map((i): string => i.src),
      );
    },
  },
  {
    key: SeoRuleKey.IMAGE_WEIGHT,
    group: G,
    run: (ctx): ReturnType<SeoRule['run']> => {
      const known = ctx.images.filter((i): boolean => i.bytes !== undefined || i.width !== undefined);
      if (known.length === 0) return null;
      const bad = known.filter(
        (i): boolean =>
          (i.bytes !== undefined && i.bytes > T.IMAGE_MAX_BYTES) ||
          (i.width !== undefined && i.width > T.IMAGE_MAX_WIDTH),
      );
      const locations = bad.map((i): string =>
        `${fileName(i.src)} : ${i.bytes !== undefined ? `${Math.round(i.bytes / 1024)} Ko` : ''} ${i.width !== undefined ? `${i.width} px` : ''}`.trim(),
      );
      const threshold = `≤ ${Math.round(T.IMAGE_MAX_BYTES / 1024)} Ko et ≤ ${T.IMAGE_MAX_WIDTH} px`;
      if (bad.length === 0) return pass('ok', threshold);
      if (bad.length === 1) return warn(1, threshold, locations);
      return fail(bad.length, threshold, locations);
    },
  },
];
